import { call, put, takeEvery, select, CallEffect, PutEffect, SelectEffect } from 'redux-saga/effects'
import { checkoutActions } from './actions'
import { shippingService, couponService, orderService } from '../../../services'
import { createPaymentIntent } from '../../../modules/payment/services/paymentService'
import { normalizeCouponData } from '../../../shared/utils/couponHelpers'
import { RootState } from '../../types'

/**
 * Saga para calcular frete
 */
function* calculateShippingSaga(action: ReturnType<typeof checkoutActions.calculateShippingRequest>): Generator<CallEffect | PutEffect | SelectEffect, void, any> {
  try {
    const address = action.payload
    
    // Obter itens do carrinho do estado
    const cartState: RootState = yield select()
    const cartItems = cartState.cart.items
    
    if (cartItems.length === 0) {
      yield put(checkoutActions.calculateShippingFailure({ error: 'Carrinho vazio' }))
      return
    }
    
    const response: any = yield call(
      async () => shippingService.calculate({
        zipCode: address.zipCode,
        items: cartItems.map(item => ({
          batteryId: item.product.id,
          quantity: item.quantity,
          weight: item.product.weight || 0
        }))
      })
    )
    
    yield put(checkoutActions.calculateShippingSuccess({
      options: response.options || []
    }))
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Erro ao calcular frete'
    yield put(checkoutActions.calculateShippingFailure({ error: errorMessage }))
  }
}

/**
 * Saga para aplicar cupom
 */
function* applyCouponSaga(action: ReturnType<typeof checkoutActions.applyCouponRequest>): Generator<CallEffect | PutEffect, void, any> {
  try {
    const { code, subtotal } = action.payload
    
    const response: any = yield call(
      async () => couponService.validate(code, { code, subtotal })
    )
    
    if (response.valid && response.coupon) {
      const normalizedCoupon = normalizeCouponData({
        ...response.coupon,
        code,
        discountAmount: response.discount
      })
      
      yield put(checkoutActions.applyCouponSuccess({ coupon: normalizedCoupon }))
    } else {
      yield put(checkoutActions.applyCouponFailure({ error: response.message || 'Cupom inválido' }))
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Erro ao aplicar cupom'
    yield put(checkoutActions.applyCouponFailure({ error: errorMessage }))
  }
}

/**
 * Saga para criar pedido
 */
function* createOrderSaga(action: ReturnType<typeof checkoutActions.createOrderRequest>): Generator<CallEffect | PutEffect, void, any> {
  try {
    const orderData = action.payload
    
    const response = yield call(orderService.create, orderData)
    
    if (response?.order?.id) {
      yield put(checkoutActions.createOrderSuccess({ orderId: response.order.id }))
    } else {
      yield put(checkoutActions.createOrderFailure({ error: 'Erro ao criar pedido' }))
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar pedido'
    yield put(checkoutActions.createOrderFailure({ error: errorMessage }))
  }
}

/**
 * Saga para criar payment intent
 */
function* createPaymentIntentSaga(action: ReturnType<typeof checkoutActions.createPaymentIntentRequest>): Generator<CallEffect | PutEffect, void, any> {
  try {
    const { amount, paymentMethod } = action.payload
    
    const response = yield call(createPaymentIntent, {
      amount,
      metadata: { paymentMethod }
    })
    
    yield put(checkoutActions.createPaymentIntentSuccess({ clientSecret: response.clientSecret }))
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar payment intent'
    yield put(checkoutActions.createPaymentIntentFailure({ error: errorMessage }))
  }
}

/**
 * Root saga do módulo Checkout
 */
export function* checkoutSaga(): Generator {
  yield takeEvery(checkoutActions.calculateShippingRequest.type, calculateShippingSaga)
  yield takeEvery(checkoutActions.applyCouponRequest.type, applyCouponSaga)
  yield takeEvery(checkoutActions.createOrderRequest.type, createOrderSaga)
  yield takeEvery(checkoutActions.createPaymentIntentRequest.type, createPaymentIntentSaga)
}

